<template>
    <fragment>
        <c-hero-full class="illustration-section-01" />

        <c-generic-section top-divider>
            <div class="container-xs">
                <h2 class="mt-0">A Licensed Customs Broker In-House</h2>

                <p align="justify">
                    Our in-house customs broker has been licensed by the Australian Border Force (ABF) since 2024, so your declarations are prepared and lodged by the same team that handles your cargo.
                </p>

                <p align="justify">
                    We have years of experience in customs brokerage and freight forwarding. Businesses across Australia rely on us for advice and assistance on customs and quarantine matters when importing and exporting goods. We are based in Perth, Western Australia.
                </p>
                <figure>
                    <c-image
                        class="image-larger"
                        :src="require('@/assets/images/Importation-image-01.jpg')"
                        alt="Licensed asbestos removal from an import container"
                        :width="712"
                        :height="400" />
                    <figcaption class="text-color-low">Licensed asbestos removal from an import container at our Kewdale yard</figcaption>
                </figure>

                <div class="imp-features">
                    <div class="imp-feature">
                        <h4>Flat Pricing</h4>
                        <p>Flat-rate fees for customs brokerage and logistics, quoted up front &mdash; no hidden charges.</p>
                        <p class="imp-zh">固定收费,报价透明,无隐藏费用</p>
                    </div>
                    <div class="imp-feature">
                        <h4>Perth Based</h4>
                        <p>We work Perth (AWST) business hours, so there is no time difference for WA importers.</p>
                        <p class="imp-zh">珀斯本地团队,西澳客户无时差沟通</p>
                    </div>
                    <div class="imp-feature">
                        <h4>Urgent Clearances</h4>
                        <p>We take urgent jobs in Perth to help you avoid port storage and container detention charges.</p>
                        <p class="imp-zh">承接加急清关,帮您避免码头堆存和滞箱费</p>
                    </div>
                </div>
            </div>
        </c-generic-section>

        <c-roadmap top-divider has-bg-color invert-color />

        <c-generic-section top-divider>
            <div class="container-xs">
                <c-section-header :data="declarationsHeader" class="center-content" />
                <div class="imp-threshold">
                    <div class="imp-threshold-card">
                        <h4>Shipments over AUD $1,000</h4>
                        <p align="justify">Goods valued above AUD $1,000 must be cleared on a formal Import Declaration (N10). Customs duty, GST and other charges are assessed before the goods are released. We classify your goods, prepare the declaration and lodge it with the ABF for you.</p>
                        <p class="imp-zh">货值超过 AUD $1,000 需提交正式进口申报(N10),关税和 GST 结清后放行;我们代为归类、制单并申报。</p>
                    </div>
                    <div class="imp-threshold-card">
                        <h4>Shipments under AUD $1,000</h4>
                        <p align="justify">Most goods valued at or below AUD $1,000 can be cleared on a Self-Assessed Clearance (SAC) declaration, and generally no customs duty or GST is payable at the border (tobacco and alcohol excepted). We lodge SAC declarations for low-value cargo.</p>
                        <p class="imp-zh">货值不超过 AUD $1,000 的货物一般以 SAC 简化申报清关,通常无需缴纳关税和 GST(烟酒除外)。</p>
                    </div>
                </div>
            </div>
        </c-generic-section>

        <c-generic-section top-divider>
            <div class="container-xs">
                <c-section-header :data="faqHeader" class="center-content" />
                <c-accordion>
                    <c-accordion-item title="Do I need to engage a customs broker?" active>
                        Unless your items are very small and/or of a personal nature, it is strongly advisable to use the services of a licensed customs broker.
                    </c-accordion-item>
                    <c-accordion-item title="What does a customs broker do?">
                        We attend to all import requirements with Customs and Quarantine on our clients' behalf.
                    </c-accordion-item>
                    <c-accordion-item title="When do I have to pay duty and/or GST?">
                        We prepare your entry prior to importation and send you an invoice for any applicable duty or GST.
                    </c-accordion-item>
                    <c-accordion-item title="How are duty and GST calculated?">
                        All items are classified under a tariff item according to their description. With the advent of Free Trade Agreements many goods are now duty free &ndash; please contact our customs team to see if your cargo qualifies for duty-free status.
                    </c-accordion-item>
                    <c-accordion-item title="Am I legally required to use a customs broker to import goods into Australia?">
                        <p align="justify">In Australia, it is not legally mandatory for individuals or businesses to engage a customs broker to import goods. However, doing so can greatly simplify the process. Customs brokers are professionals who are knowledgeable about the rules and regulations pertaining to the import of goods into the country, including tariff classifications, duty rates, and any applicable taxes or fees. They can help ensure that your goods are cleared through customs efficiently and in compliance with all Australian laws and regulations.</p>

                        <p align="justify">Importers who choose to clear goods on their own must ensure they are fully aware of and comply with all customs requirements, which can be complex and time-consuming. This includes correctly classifying the goods, calculating and paying the correct taxes and duties, and completing all necessary documentation. Mistakes in any of these areas can lead to delays, additional costs, or even penalties.</p>

                        <p align="justify">Therefore, while not obligatory, many importers find that using a customs broker is a valuable investment that can save time and protect against compliance risks. It is particularly recommended for those who are not familiar with the import process or who are dealing with a large volume of goods or complex items that might require special handling or additional paperwork.</p>
                    </c-accordion-item>
                    <c-accordion-item title="What is the difference between a customs broker and a freight forwarder?">
                        <p align="justify">Customs brokers and freight forwarders play distinct roles in the logistics and importation process.</p>

                        <p align="justify">A freight forwarder specialises in the logistics of shipping goods from one point to another. They act as an intermediary between the shipper and various transportation services such as ocean shipping on cargo ships, trucking, expedited shipping by air freight, and moving goods by rail. Freight forwarders have expertise in negotiating the best prices, selecting reliable transport providers, and determining the most efficient and cost-effective routes. They handle all aspects of the shipping process, including consolidation of freight, cargo insurance, and packing, ensuring that goods arrive at their intended destination safely and on time.</p>

                        <p align="justify">On the other hand, a customs broker is a licensed professional who focuses on the customs clearance aspect of the import process. Their primary role is to ensure that imported goods comply with all the applicable customs regulations and laws in Australia. Customs brokers are responsible for accurately classifying goods according to the Harmonized System (HS) codes, calculating and paying duties and taxes, preparing and submitting necessary documentation, and communicating with customs authorities. They help importers navigate the complexities of customs procedures, avoid costly delays, and prevent potential legal issues related to non-compliance.</p>
                    </c-accordion-item>
                </c-accordion>
            </div>
        </c-generic-section>

        <c-testimonial />
        <c-cta top-divider bottom-divider split />
    </fragment>
</template>

<script>
// import layout
import CLayout from '@/layouts/LayoutDefault.vue'
// import section header
import CSectionHeader from '@/components/sections/partials/SectionHeader.vue'
// import sections
import CHeroFull from '@/components/sections/HeroFull.vue'
import CGenericSection from '@/components/sections/GenericSection.vue'
import CRoadmap from '@/components/sections/Roadmap.vue'
import CTestimonial from '@/components/sections/Testimonial.vue'
import CCta from '@/components/sections/Cta.vue'
// import some required elements
import CImage from '@/components/elements/Image.vue'
import CAccordion from '@/components/elements/Accordion.vue'
import CAccordionItem from '@/components/elements/AccordionItem.vue'

export default {
  name: 'Importation',
  components: {
    CSectionHeader,
    CHeroFull,
    CGenericSection,
    CRoadmap,
    CTestimonial,
    CImage,
    CCta,
    CAccordion,
    CAccordionItem
  },
  data() {
    return {
      declarationsHeader: {
        title: 'Import Declarations',
        paragraph: 'What changes at the AUD $1,000 threshold'
      },
      faqHeader: {
        title: 'Importation FAQ'
      }
    }
  },
  created() {
    this.$emit('update:layout', CLayout)
    document.title = 'Customs Clearance — Line-1 Logistics'
  }
}
</script>

<style scoped>
.imp-features {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    margin-top: 32px;
}
.imp-feature h4 {
    margin: 0 0 8px;
}
.imp-feature p {
    margin: 0;
}
.imp-threshold {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    text-align: left;
}
.imp-threshold-card {
    border: 1px solid rgba(128, 128, 128, .3);
    border-radius: 6px;
    padding: 20px 24px;
}
.imp-threshold-card h4 {
    margin-top: 0;
}
.imp-zh {
    font-size: 13px;
    opacity: .7;
    margin-top: 6px;
}
@media (max-width: 640px) {
    .imp-features,
    .imp-threshold {
        grid-template-columns: 1fr;
    }
}
</style>
